// Datum Store - manages datum (data backing for widgets)

import { create } from 'zustand';
import type { Datum } from '../api/datum';
import { listDatumForUser, getDatum, listDatumByPrincipal } from '../api/datum';

interface DatumState {
  // All datum for current user (user + cohorts)
  datum: Datum[];
  datumLoading: boolean;
  datumError: string | null;

  // Cached datum by ID for fast widget access
  datumById: Map<string, Datum>;

  // Current user ID (used for loading)
  currentUserId: string | null;

  // Datum by principal (for library view)
  datumByPrincipal: Map<string, Datum[]>;
  principalLoading: Map<string, boolean>;

  // Actions
  loadDatumForUser: (tenant: string) => Promise<void>;
  loadDatumByPrincipal: (tenant: string, principalId: string, kind?: string) => Promise<void>;
  refreshDatum: (tenant: string, datumId: string) => Promise<void>;

  // Helpers to get datum by kind or ID
  getDatumByKind: (kind: string) => Datum[];
  getDatum: (datumId: string) => Datum | undefined;
  getDatumByPrincipal: (principalId: string) => Datum[];

  reset: () => void;
}

export const useDatumStore = create<DatumState>((set, get) => ({
  datum: [],
  datumLoading: false,
  datumError: null,
  datumById: new Map(),
  currentUserId: null,
  datumByPrincipal: new Map(),
  principalLoading: new Map(),

  loadDatumForUser: async (tenant) => {
    set({ datumLoading: true, datumError: null });
    try {
      const response = await listDatumForUser(tenant);

      // Cache datum by ID
      const datumById = new Map<string, Datum>();
      response.signals.forEach((signal) => {
        datumById.set(signal.id, signal);
      });

      set({
        datum: response.signals,
        datumById: datumById,
        datumLoading: false,
      });
    } catch (error) {
      console.error('Failed to load datum:', error);
      set({
        datumError: error instanceof Error ? error.message : 'Failed to load datum',
        datumLoading: false,
      });
    }
  },

  loadDatumByPrincipal: async (tenant, principalId, kind) => {
    const principalLoading = new Map(get().principalLoading);
    principalLoading.set(principalId, true);
    set({ principalLoading });

    try {
      const response = await listDatumByPrincipal(tenant, principalId, kind);

      // Cache datum by ID
      const datumById = new Map(get().datumById);
      response.signals.forEach((signal) => {
        datumById.set(signal.id, signal);
      });

      // Store by principal
      const datumByPrincipal = new Map(get().datumByPrincipal);
      datumByPrincipal.set(principalId, response.signals);

      principalLoading.delete(principalId);

      set({
        datumById,
        datumByPrincipal,
        principalLoading,
      });
    } catch (error) {
      console.error('Failed to load datum by principal:', error);
      const principalLoading = new Map(get().principalLoading);
      principalLoading.delete(principalId);
      set({ principalLoading });
    }
  },

  refreshDatum: async (tenant, datumId) => {
    try {
      const response = await getDatum(tenant, datumId);
      const updatedDatum = response.signal;

      // Update in both array and cache
      const datum = get().datum.map((d) =>
        d.id === datumId ? updatedDatum : d
      );

      const datumById = new Map(get().datumById);
      datumById.set(datumId, updatedDatum);

      set({ datum, datumById });
    } catch (error) {
      console.error('Failed to refresh datum:', error);
    }
  },

  getDatumByKind: (kind) => {
    return get().datum.filter((s) => s.kind === kind);
  },

  getDatum: (signalId) => {
    return get().datumById.get(signalId);
  },

  getDatumByPrincipal: (principalId) => {
    return get().datumByPrincipal.get(principalId) || [];
  },

  reset: () => {
    set({
      datum: [],
      datumLoading: false,
      datumError: null,
      datumById: new Map(),
      currentUserId: null,
      datumByPrincipal: new Map(),
      principalLoading: new Map(),
    });
  },
}));
